<template>
	<VPopup
		ref="popup"
		class="comments-popup"
		show-pointer
		position="right"
		:offset="10"
		:target="target"
		:visible="visible"
		@update:visible="onPopupVisibleChange"
	>
		<VTextarea
			ref="textarea"
			v-model="body"
			placeholder="Введите комментарий"
			:min-height="77"
		/>

		<div class="form-action-wrap">
			<VLink
				:disabled="isAddingComment"
				@click="save"
			>
				Добавить комментарий
			</VLink>
			<span class="description">Ctrl + Enter</span>
		</div>

		<div
			v-if="isLoadingComments"
			class="comments-history loading"
		>
			<VLoader/>
		</div>

		<template v-else-if="comments.length > 0">
			<div
				ref="commentHistory"
				class="comments-history"
				:class="{'show-all': showAllComments}"
			>
				<article
					v-for="(item, index) in comments"
					:key="item.id ?? `local-${index}`"
					:data-comment-id="item.id"
					class="comments-history-item"
					:class="{
						'my':       item.isMine,
						'is-saving': savingComments.has(item),
						'read': item.isMine || item.isRead
					}"
				>
					<header>
						<div class="author">
							{{ item.createdBy?.name }}
						</div>
						<div class="created-time">
							{{ Utils.date.humanizeDate(item.createdAt) }}
						</div>
					</header>

					<div
						class="body"
						:class="{ 'in-edit-mode': activeEditComment !== null }"
					>
						<template v-if="activeEditComment !== null && activeEditComment.id === item.id">
							<VTextarea
								v-model="activeEditComment.body"
								:min-height="48"
							/>
						</template>
						<div
							v-else
							class="body-text"
						>
							<span v-html="parseLinks(item.body)" @click.stop="handleLinkClick"/>
						</div>

						<!--
							Кнопки действий показываем только для комментов
							которые: (а) свои, (б) сохранены, (в) не "в полёте",
							(г) ещё в окне редактирования (25 минут от createdAt).
						-->
						<div
							v-if="canEdit(item)"
							class="actions-wrap"
						>
							<div v-if="activeEditComment === null || activeEditComment.id !== item.id">
								<VLink @click.stop="startEditComment(item)">
									<template #icon>
										<VIcon material="edit" theme="outlined"/>
									</template>
									Редактировать
								</VLink>

								<VLink @click="entity.isExisting ? deleteServerComment(item) : deleteLocalComment(index)">
									<template #icon>
										<VIcon material="delete" theme="outlined"/>
									</template>
									Удалить
								</VLink>
							</div>
							<div v-else>
								<VLink @click="updateComment">
									Сохранить
								</VLink>
								<VLink @click="stopEditComment">
									Отмена
								</VLink>
							</div>
						</div>
					</div>
				</article>
			</div>
		</template>

		<div
			v-else
			class="comments-history empty"
		>
			Комментариев пока нет
		</div>

		<div
			v-if="canShowAllButton"
			class="comment-footer"
		>
			<div class="footer-btn-group">
				<VLink
					class="see-all-btn"
					:disabled="showAllComments"
					@click="loadAllComments"
				>
					<template #icon>
						<VIcon material="visibility" theme="outlined"/>
					</template>
					Показать все
				</VLink>
				<div
					class="read-all-btn"
					title="Отметить все как прочитанные"
					@click="markAllAsRead"
				>
					<VIcon material="check_circle_outline" theme="outlined"/>
				</div>
			</div>
		</div>
	</VPopup>
</template>

<style lang="scss" src="./CommentsPopup.scss"/>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import Utils              from '@/core/utils/Utils';
import TimeStamp          from '@/core/types/TimeStamp';
import BaseEntity         from '@/core/misc/BaseEntity';
import Comment            from '@/core/services/Comments/Comment';
import CommentsService    from '@/core/services/Comments/CommentsService';
import CommentsStatus     from '@/core/services/Comments/CommentsStatus';
import EntityPointer      from '@/domains/EntityPointer/EntityPointer';

/** Краткий лимит при первой загрузке. "Показать все" грузит всё. */
const PREVIEW_LIMIT = 5;
const ALL_LIMIT     = 1000;

export default defineComponent({
	name: 'CommentsPopup',

	props: {
		/** Тип сущности на бэке (например 'assembly', 'order', 'paymentRequest') */
		entityTypeName: {
			type:     String,
			required: true,
		},
		entity: {
			type:     Object as PropType<BaseEntity>,
			required: true,
		},
		/** DOM-элемент или CSS-селектор кнопки-триггера. Передаётся в VPopup */
		target: {
			type:    [Object, String],
			default: null,
		},
		/** Видимость попапа (управляется родителем) */
		visible: {
			type:    Boolean,
			default: false,
		},
	},

	emits: ['update:visible'],

	data() {
		return {
			Utils,

			body: '',

			/** Видимый список комментов в попапе. Для серверного режима — копия с бэка, для локального — ссылка на entity.commentaries */
			comments: [] as Comment[],

			/** Идёт первая загрузка списка с сервера */
			isLoadingComments: false,

			/** Идёт сохранение нового коммента (блокирует кнопку Добавить) */
			isAddingComment: false,

			/** Комменты "в полёте" — для оптимистичного UX. Поиск по ссылке на объект. */
			savingComments: new Set<Comment>(),

			/** Редактируемый сейчас коммент (клон, чтобы не пачкать оригинал до Save) */
			activeEditComment: null as Comment | null,

			/** Загружены ли все комменты (false = только превью) */
			showAllComments: false,
		};
	},

	computed: {
		/**
		 * EntityPointer для серверных вызовов. typeName приходит явным пропом —
		 * родитель отвечает за то чтобы он совпадал с lcfirst($entity::class) на бэке.
		 */
		parentEntity(): EntityPointer {
			return new EntityPointer(this.entityTypeName, this.entity.id);
		},

		/** Кнопка "Показать все" нужна только если есть скрытые комменты */
		canShowAllButton(): boolean {
			if (!this.entity.isExisting) return false;
			if (this.showAllComments)    return false;

			const total = this.entity.commentsStatus?.qty ?? 0;
			return total > PREVIEW_LIMIT;
		},
	},

	watch: {
		visible: {
			immediate: true,
			handler(value: boolean) {
				if (value) {
					this.onShow();
				} else {
					this.onHide();
				}
			},
		},
	},

	methods: {

		// ============================================================
		// Lifecycle (показ/скрытие попапа)
		// ============================================================

		async onShow() {
			window.addEventListener('keydown', this.onKeyDown);
			this.focusTextarea();

			if (this.entity.isExisting) {
				const loaded = await this.loadComments(PREVIEW_LIMIT);

				// Помечаем все прочитанными при открытии — но только если есть непрочитанные
        // Если вернулся ответ с ошибкой (AccessViolation) не запускать this.markAllAsRead
				if (loaded && this.entity.commentsStatus?.isUnread) {
					this.markAllAsRead();
				}
        //а также прячем футер, где есть кнопки Показать все и пометить все как прочитанное
        if(loaded === false) {
          this.showAllComments = true;
        }
			} else {
				// Локальный режим — подхватываем буфер новой сущности
				this.comments = this.entity.commentaries;
			}
		},

		onHide() {
			window.removeEventListener('keydown', this.onKeyDown);

			this.body              = '';
			this.activeEditComment = null;
			this.showAllComments   = false;
		},

		onPopupVisibleChange(value: boolean) {
			this.$emit('update:visible', value);
		},

		onKeyDown(event: KeyboardEvent) {
			if (event.key === 'Enter' && event.ctrlKey) {
				this.save();
			} else if (event.key === 'Escape') {
				this.$emit('update:visible', false);
			}
		},

		focusTextarea() {
			this.$nextTick(() => {
				const ref = this.$refs.textarea as any;
				if (this.$core.utils.isObject(ref)) {
					const el = ref.$el.querySelector('textarea');
					el?.focus();
				}
			});
		},

		// ============================================================
		// Загрузка
		// ============================================================

		/**
		 * Загружает комменты с сервера. Если qty=0 — не делает запроса, при этом возвращает true и присваивает this.comments = []
     * Возвращает true и присваивает this.comments = []
     * Возвращает false при ошибке загрузки (в т.ч. AccessViolation) —
     * вызывающий код по этому флагу решает, продолжать ли (например, markAllAsRead).
		 */
		async loadComments(limit: number): Promise<boolean> {
			const total = this.entity.commentsStatus?.qty ?? 0;

			if (total === 0) {
				this.comments = [];
				return true;
			}

			this.isLoadingComments = true;
      let hasError = false;

			try {
        this.comments = await CommentsService.getListPaginated(
          {
            parentEntity: this.parentEntity,
            pagination:   { limit, offset: 0 },
          },
          false,
          (error) => {
            hasError = true;
          }
        );

        //при загрузке комментариев произошла ошибка доступа (или любая другая),
        // такак комментарии могут читать только админы,

        if (hasError === true) {
          return false;
        }

				// При полной загрузке синхронизируем qty с фактом — могли разойтись с реестром
				// (например пока юзер открывал попап, кто-то удалил коммент).
				// При превью этого делать нельзя — мы видим только первые PREVIEW_LIMIT.
				if (this.showAllComments && this.entity.commentsStatus) {
					this.entity.commentsStatus.qty = this.comments.length;
				}

        return true;
			} finally {
				this.isLoadingComments = false;
			}
		},

		async loadAllComments() {
			this.showAllComments = true;
			await this.loadComments(ALL_LIMIT);
		},

		// ============================================================
		// Добавление
		// ============================================================

		async save() {
			if (this.isAddingComment) return;
			if (!this.$core.utils.string.isNotEmptyString(this.body)) return;

			if (this.entity.isExisting) {
				await this.saveServerComment();
			} else {
				this.saveLocalComment();
			}
		},

		/**
		 * Серверный режим: оптимистичная вставка коммента с пометкой "в полёте",
		 * после успешного RPC заменяем поля из ответа.
		 */
		async saveServerComment() {
			const optimisticComment = Comment.fromNew(this.parentEntity, this.body);
			optimisticComment.createdBy = this.$core.currentUser;
			optimisticComment.createdAt = new TimeStamp();

			this.comments.unshift(optimisticComment);
			this.savingComments.add(optimisticComment);
			this.body = '';
			this.isAddingComment = true;

			try {
				const savedComment = await CommentsService.add(optimisticComment);

				const index = this.comments.indexOf(optimisticComment);

				if (savedComment) {
					if (index !== -1) {
						this.comments.splice(index, 1, savedComment);
					}
					// Бэк сам пометил коммент прочитанным для автора — обновляем локальную мету.
					// ensureCommentsStatus создаёт статус если бэк не присылал (например первая
					// сборка с комментами вообще).
					this.entity.ensureCommentsStatus().registerOwnAdd();
				} else {
					// Ошибка — откатываем вставку (нотификацию уже показал сервис)
					if (index !== -1) {
						this.comments.splice(index, 1);
					}
				}
			} finally {
				this.savingComments.delete(optimisticComment);
				this.isAddingComment = false;
			}
		},

		/**
		 * Локальный режим: коммент уходит в entity.commentaries и будет создан
		 * на бэке вместе с сохранением сущности (через CommentsService::addComments на PHP).
		 */
		saveLocalComment() {
			const comment = Comment.fromNew(this.parentEntity, this.body);
			comment.createdBy = this.$core.currentUser;
			comment.createdAt = new TimeStamp();

			this.entity.commentaries.push(comment);
			this.body = '';
		},

		// ============================================================
		// Редактирование
		// ============================================================

		startEditComment(comment: Comment) {
			this.activeEditComment = this.$core.utils.cloneDeep(comment);
		},

		stopEditComment() {
			this.activeEditComment = null;
		},

		async updateComment() {
			if (this.activeEditComment === null) return;
			if (!this.$core.utils.string.isNotEmptyString(this.activeEditComment.body)) return;

			if (this.entity.isExisting) {
				const success = await CommentsService.update(this.activeEditComment);

				if (success) {
					const index = this.comments.findIndex(item => item.id === this.activeEditComment!.id);
					if (index !== -1) {
						this.comments[index].body      = this.activeEditComment.body;
						this.comments[index].updatedAt = new Date();
					}
					this.stopEditComment();
				}
			} else {
				// Локальный режим — правим объект в entity.commentaries напрямую
				const index = this.entity.commentaries.findIndex(item => item === this.activeEditComment);
				if (index !== -1) {
					this.entity.commentaries[index].body      = this.activeEditComment.body;
					this.entity.commentaries[index].updatedAt = new Date();
				}
				this.stopEditComment();
			}
		},

		// ============================================================
		// Удаление
		// ============================================================

		/**
		 * Серверный режим: оптимистичное удаление с откатом на ту же позицию при ошибке.
		 */
		async deleteServerComment(comment: Comment) {
			const index = this.comments.indexOf(comment);
			if (index === -1) return;

			this.comments.splice(index, 1);

			const success = await CommentsService.delete(comment.id as number);

			if (success) {
				this.entity.commentsStatus?.registerDelete();
			} else {
				// Откатываем удаление (нотификацию уже показал сервис)
				this.comments.splice(index, 0, comment);
			}
		},

		/**
		 * Локальный режим: удаление из буфера entity.commentaries по индексу.
		 * Сервер тут вообще не при чём — этого коммента нигде кроме фронта не существует.
		 */
		deleteLocalComment(index: number) {
			this.entity.commentaries.splice(index, 1);
		},

		// ============================================================
		// Прочтение
		// ============================================================

		async markAllAsRead() {
			const success = await CommentsService.markAllAsRead(this.parentEntity);

			if (success && this.entity.commentsStatus) {
				// Заменяем весь объект, а не мутируем поля
				const updated = new CommentsStatus();
				updated.qty         = this.entity.commentsStatus.qty;
				updated.qtyRead     = this.entity.commentsStatus.qty;
				updated.isUnread    = false;
				updated.lastComment = this.entity.commentsStatus.lastComment;

				this.entity.commentsStatus = updated;
			}

      if (success) {
        // Локально помечаем все непрочитанные как прочитанные — на бэк это
        // уже ушло CommentsService.markAllAsRead, тут только синхронизация
        // локального состояния попапа с уже случившимся на сервере фактом.
        this.comments.forEach(item => {
          item.isRead = true;
        })
      }
		},

		// ============================================================
		// Хелперы
		// ============================================================

		/**
		 * Можно ли показать кнопки редактирования/удаления коммента.
		 * Внутренние свойства коммента — в comment.isEditable.
		 * Здесь только внешние обстоятельства от компонента.
		 */
		canEdit(comment: Comment): boolean {
			// Локальный режим — кнопка всегда доступна, никаких 25 минут
			// (на бэке коммент ещё не сохранён, ограничения не применимы)
			if (!this.entity.isExisting) return true;

			// Коммент в полёте — кнопок нет до подтверждения сервером
			if (this.savingComments.has(comment)) return false;

			return comment.isEditable;
		},

		// ============================================================
		// Парсинг ссылок в теле коммента
		// ============================================================

		parseLinks(text: string | null): string {
			if (!text) return '';
			const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g;
			const escaped  = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
			return escaped.replace(urlRegex, url => {
				return `<a href="${url}" class="comment-link" data-url="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
			});
		},

		handleLinkClick(event: MouseEvent) {
			const link = (event.target as HTMLElement).closest('a.comment-link') as HTMLAnchorElement | null;
			if (!link) return;
			event.preventDefault();
			const url = link.dataset.url;
			if (url) window.open(url, '_blank', 'noopener,noreferrer');
		},
	},
});
</script>
